import React, { useState, useEffect, useMemo } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  FaRegArrowAltCircleDown,
  FaRegArrowAltCircleUp,
  FaRegMoneyBillAlt,
  FaInfoCircle,
  FaChartPie,
  FaExchangeAlt,
  FaRegEdit,
  FaDownload,
} from "react-icons/fa";
import {
  BsBoxArrowInDown,
  BsBoxArrowUpRight,
  BsArrowDownCircle,
  BsArrowUpCircle,
  BsBoxArrowInDownRight,
} from "react-icons/bs";
import { AiOutlineSearch, AiOutlineFundProjectionScreen } from "react-icons/ai";
import { MdOutlineFilterList, MdPrint, MdOutlineInsights } from "react-icons/md";
import { IoWalletSharp } from "react-icons/io5";
import { TbZoomMoney, TbHistory } from "react-icons/tb";
import { GiWallet } from "react-icons/gi";
import { Pie, Line, Bar, Doughnut } from "react-chartjs-2";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Filler,
} from "chart.js";
import axios from "axios";
import clsx from "clsx";
import { HiOutlineCurrencyRupee } from "react-icons/hi2";
import { fetchStocknames } from '../data/Stocknames';
// Register chart.js elements
ChartJS.register(
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Filler
);

interface Party {
  id: number;
  name: string;
  date: string;
  number?: string;
  contact_person_name?: string;
  contact_person_number?: string;
  categories?: string | string[];
  total_amount?: number;
}
interface Stock {
  id: number | string;
  party_id: number;
  categories: any[];
  date?: string;
  total_amount: number | string;
  indirect_charges?: number | string;
}
interface Withdrawal {
  id: number;
  party_id: number;
  amount: number;
  date: string;
  catrgories?: string;
}
interface StockHistory {
  id: number;
  stock_id: string | number;
  cat_id?: string | number;
  current_price: number | string;
  current_price_date: string;
}
interface Category {
  id: string | number;
  category_name: string;
}

type TransactionType = "buy" | "sell" | "withdraw";
interface Transaction {
  id: number | string;
  party_id: number | string;
  date: string;
  type: TransactionType;
  investment_type?: string; // New field
  stock_id?: number | string;
  category_id?: number | string;
  qty: number;
  unit?: string;
  amount: number;
  remark?: string;
  current_price?: number;
  indirect_charges?: number;

}
type FilterState = {
  from?: string;
  to?: string;
  search?: string;
  type?: string;
  stock_id?: string;
  category_id?: string;
  party_id?: string | number;
  minAmount?: string;
  maxAmount?: string;
  quick?: string;
};

// === HELPERS ===
function formatMoney(
  val: number | undefined,
  opts?: { compact?: boolean; symbol?: boolean }
) {
  if (!val || isNaN(val)) return opts?.symbol ? "₹0" : "0";
  if (opts?.compact) {
    const absVal = Math.abs(val);
    if (absVal >= 1e7)
      return (opts?.symbol ? "₹" : "") + (val / 1e7).toFixed(2) + "Cr";
    if (absVal >= 1e5)
      return (opts?.symbol ? "₹" : "") + (val / 1e5).toFixed(2) + "L";
    if (absVal >= 1e3)
      return (opts?.symbol ? "₹" : "") + (val / 1e3).toFixed(1) + "k";
  }
  return (
    (opts?.symbol ? "₹" : "") +
    Number(val).toLocaleString("en-IN", { maximumFractionDigits: 2 })
  );
}
function downloadCSV(transactions: Transaction[], parties: Party[], format: "csv" | "excel" = "csv") {
  let text = "";
  if (format === "csv") {
    const csvHeader = "Date,Stock,Category,Party,Type,Qty,Unit,Amount,Remark\n";
    const rows = transactions.map((t) => {
      const partyObj = parties.find((p) => String(p.id) === String(t.party_id));
      return [
        t.date,
        t.stock_id,
        t.category_id,
        typeof partyObj?.name === "object" ? "" : partyObj?.name ?? "",
        t.type,
        t.qty,
        typeof t.unit === "object" ? "-" : t.unit ?? "-",
        t.amount,
        typeof t.remark === "object" ? "-" : t.remark ?? "",
      ].join(",");
    });
    text = csvHeader + rows.join("\n");
  } else {
    const excelHeader = "Date\tStock\tCategory\tParty\tType\tQty\tUnit\tAmount\tRemark\n";
    const rows = transactions.map((t) => {
      const partyObj = parties.find((p) => String(p.id) === String(t.party_id));
      return [
        t.date,
        t.stock_id,
        t.category_id,
        typeof partyObj?.name === "object" ? "" : partyObj?.name ?? "",
        t.type,
        t.qty,
        typeof t.unit === "object" ? "-" : t.unit ?? "-",
        t.amount,
        typeof t.remark === "object" ? "-" : t.remark ?? "",
      ].join("\t");
    });
    text = excelHeader + rows.join("\n");
  }
  const blob = new Blob([text], {
    type:
      format === "csv"
        ? "text/csv"
        : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `transactions_${Date.now()}.${format === "csv" ? "csv" : "xls"}`;
  link.click();
  URL.revokeObjectURL(url);
}

function TransactionDetailModal({
  transaction,
  onClose,
  stock,
  category,
  party,
}: {
  transaction: Transaction;
  onClose: () => void;
  stock: Stock | undefined;
  category: Category | any;
  party: Party | undefined;
}) {
  if (!transaction) return null;
  return (
    <div className="fixed z-50 inset-0 bg-[#1119]/50 flex items-center justify-center">
      <div className="bg-white shadow-2xl text-[#374151] p-6 rounded-lg min-w-[340px] max-w-[96vw] relative animate-fade-in">
        <button
          className="absolute top-2 right-2 text-xl text-[#868cab] hover:text-[#222] p-0.5"
          onClick={onClose}
        >
          ×
        </button>
        <div className="flex items-center gap-2 mb-4">
          <FaInfoCircle className="text-[#38b6ff] text-xl" />
          <span className="font-semibold text-lg tracking-wide">
            Transaction Detail
            <span className="ml-2 inline-flex items-center gap-1 px-2 py-1 rounded bg-[#f6f8fc] text-[#8ca8e2] text-xs font-bold">
              #{transaction.id}
            </span>
          </span>
        </div>
        <dl className="flex flex-wrap gap-2 mb-2 text-[13px]">
          <dt className="w-28 text-[#555] font-semibold">Type</dt>
          <dd>
            <span
              className={
                "px-2 py-1 rounded " +
                (transaction.type === "buy"
                  ? "bg-green-100 text-green-700"
                  : transaction.type === "sell"
                  ? "bg-red-100 text-red-600"
                  : "bg-yellow-50 text-yellow-800")
              }
            >
              {transaction.type.charAt(0).toUpperCase() + transaction.type.slice(1)}
            </span>
          </dd>
          <dt className="w-28 text-[#555] font-semibold">Date</dt>
          <dd>{transaction.date}</dd>
          <dt className="w-28 text-[#555] font-semibold">Stock</dt>
          <dd>
            {typeof transaction.stock_id === "string" && transaction.stock_id
              ? transaction.stock_id
              : typeof stock?.id === "string" || typeof stock?.id === "number"
                ? stock?.id
                : "-"}
          </dd>
          <dt className="w-28 text-[#555] font-semibold">Category</dt>
          <dd>
            {typeof transaction.category_id === "string" && transaction.category_id
              ? transaction.category_id
              : typeof category?.category_name === "string"
                ? category.category_name
                : typeof category?.category === "string"
                  ? category.category
                  : "-"}
          </dd>
          <dt className="w-28 text-[#555] font-semibold">Party</dt>
          <dd>
            {typeof transaction.party_id === "string" && transaction.party_id
              ? transaction.party_id
              : typeof party?.name === "string"
                ? party.name
                : "-"}
          </dd>
          <dt className="w-28 text-[#555] font-semibold">Quantity</dt>
          <dd>{transaction.qty}</dd>
          <dt className="w-28 text-[#555] font-semibold">Unit</dt>
          <dd>
            {typeof transaction.unit === "object" ? "-" : transaction.unit ?? "-"}
          </dd>
          <dt className="w-28 text-[#555] font-semibold">Amount</dt>
          <dd>{formatMoney(transaction.amount, { symbol: true })}</dd>
          {transaction.type === "sell" && (
            <>
              <dt className="w-28 text-[#555] font-semibold">Indirect Charges</dt>
              <dd>
                {typeof transaction.indirect_charges === "number" || typeof transaction.indirect_charges === "string"
                  ? formatMoney(Number(transaction.indirect_charges), { symbol: true })
                  : "-"}
              </dd>
            </>
          )}
          <dt className="w-28 text-[#555] font-semibold">Remark</dt>
          <dd>
            {typeof transaction.remark === "object" ? "-" : transaction.remark ?? "-"}
          </dd>
        </dl>
        <div className="flex gap-2 mt-5">
          <button
            className="bg-[#eef2fd] border rounded px-3 py-1.5 text-xs font-semibold text-[#4e6189] hover:bg-[#e6f4ff]"
            onClick={() => {
              window.print();
            }}
          >
            <MdPrint className="inline mr-1 -mt-0.5" /> Print
          </button>
          <button
            className="bg-[#f7f7fb] border rounded px-3 py-1.5 text-xs font-semibold text-[#3e987a] hover:bg-[#e0fcf5]"
            onClick={() =>
              navigator.clipboard
                .writeText(JSON.stringify(transaction, null, 2))
                .then(() => alert("Transaction copied to clipboard!"))
            }
          >
            <FaRegEdit className="inline mr-1 -mt-0.5" /> Copy JSON
          </button>
        </div>
      </div>
    </div>
  );
}

// --- TransactionEditModal: use name INSTEAD of ids for party, stock, category ---



function TransactionEditModal({
  transaction,
  onClose,
  onSave,
}: {
  transaction: Transaction;
  onClose: () => void;
  onSave: (t: Transaction) => Promise<void>;
}) {
  // Main state for all form fields
  const [formdata, setFormData] = useState<any>({
    ...transaction,
    stock_id: transaction.stock_id ?? "",
    category_id: transaction.category_id ?? "",
    party_id: transaction.party_id ?? "",
  });

  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [stockNames, setStockNames] = useState<string[]>([]);
  const [allParties, setAllParties] = useState<Party[]>([]);
  const [allCategories, setAllCategories] = useState<Category[]>([]);
  const [allStocks, setAllStocks] = useState<Stock[]>([]);

  const { stock, category } = findStockAndCategory(formdata.stock_id, formdata.category_id);

  async function fetchAllData() {
    try {
      const partiesRes = await axios.get(
        `${process.env.REACT_APP_API_URL}/auth/addparty`
      );
      setAllParties(partiesRes.data || []);

      const stocksRes = await axios.get(
        `${process.env.REACT_APP_API_URL}/auth/addstocks`
      );
      let stocksArr = Array.isArray(stocksRes.data) ? stocksRes.data : [];
      stocksArr = stocksArr.map((stock: any) => ({
        ...stock,
        categories: (() => {
          try {
            if (Array.isArray(stock.categories)) return stock.categories;
            if (typeof stock.categories === "string" && stock.categories.length > 0)
              return JSON.parse(stock.categories);
            return [];
          } catch {
            return [];
          }
        })(),
      }));
      setAllStocks(stocksArr);
    } catch (e) {
    }
  }

  useEffect(() => {
    fetchAllData();
  }, []);

  function findStockAndCategory(
    stock_identifier: string | number | undefined,
    category_id: string | number | undefined
  ) {
    const stock = allStocks.find((s: any) =>
      String(s.id) === String(stock_identifier) ||
      s.categories.some((cat: any) => cat.stock_name === stock_identifier)
    );

    let category = null;
    if (stock && Array.isArray(stock.categories)) {
      category = stock.categories.find((cat: any) => {
        const isMatch =
          String(cat.id ?? cat.category_name) === String(category_id) ||
          cat.stock_name === stock_identifier;
        return isMatch;
      });
    }

    if (!category && category_id) {
      category = allCategories.find(
        (c) => String(c.id) === String(category_id) || c.category_name === category_id
      );
    }

    if (!category && category_id) {
      category = { category_name: category_id };
    }

    return { stock, category };
  }

  // Fetches
  useEffect(() => {
    (async () => {
      const stocks = await fetchStocknames();
      setStockNames(
        stocks
          .map(
            (s: any) => s.name || s.stock_name || s.stockName || s.id || ""
          )
          .filter(Boolean)
      );
    })();
    (async () => {
      try {
        const partiesRes = await axios.get(
          `${process.env.REACT_APP_API_URL}/auth/addparty`
        );
        setAllParties(Array.isArray(partiesRes.data) ? partiesRes.data : []);
      } catch { }
    })();
    (async () => {
      try {
        const categoriesRes = await axios.get(
          `${process.env.REACT_APP_API_URL}/auth/categories`
        );
        setAllCategories(
          Array.isArray(categoriesRes.data) ? categoriesRes.data : []
        );
      } catch { }
    })();
  }, []);

  // Handles any field changes
  function handleChange(field: keyof Transaction, value: any) {
    setFormData((prev: any) => ({
      ...prev,
      [field]:
        field === "qty"
          ? Number(value)
          : field === "amount"
            ? Number(value)
            : field === "indirect_charges"
              ? Number(value)
              : value,
    }));
  }

  //console.log("stock id ", formdata.stock_id, "category id, ", formdata.category_id);

  useEffect(() => {
    if (!category) return;

    setFormData((prev: any) => {
      let updated = { ...prev };

      // ----- Stock ID logic -----
      // If stock_id is a number (not empty string and not NaN), use from category.stock_name
      if (
        (typeof prev.stock_id === "number" && !isNaN(prev.stock_id)) ||
        (typeof prev.stock_id === "string" && prev.stock_id.trim() !== "" && !isNaN(Number(prev.stock_id)) && prev.stock_id !== "0")
      ) {
        if (category.stock_name) {
          updated.stock_id = category.stock_name;
        }
      }
      // If stock_id is empty → take from category.stock_name (fallback)
      if ((!prev.stock_id || prev.stock_id === "") && category.stock_name) {
        updated.stock_id = category.stock_name;
      }

      // ----- Category ID logic (same as stock_id logic above, but for categories) -----
      // If category_id is a number (not empty string and not NaN), use from category.category or category.category_name
      if (
        (typeof prev.category_id === "number" && !isNaN(prev.category_id)) ||
        (typeof prev.category_id === "string" && prev.category_id.trim() !== "" && !isNaN(Number(prev.category_id)) && prev.category_id !== "0")
      ) {
        if (category.category || category.category_name) {
          updated.category_id = category.category || category.category_name;
        }
      }
      // If category_id is empty → take from category.category or category.category_name (fallback)
      if ((!prev.category_id || prev.category_id === "") && (category.category || category.category_name)) {
        updated.category_id = category.category || category.category_name;
      }

      return updated;
    });
  }, [category]);

  return (
    <div className="fixed z-50 inset-0 bg-[#1119]/60 flex items-center justify-center">
      <div className="bg-gradient-to-br from-[#f3edfa] via-[#e9f3fd] to-[#f1fbf1] shadow-2xl text-[#222d38] px-0 py-0 rounded-2xl min-w-[420px] max-w-[98vw] w-[600px] relative animate-fade-in border-2 border-[#e4e3f5]">
        {/* Header */}
        <div className="bg-gradient-to-r from-[#faecd7] to-[#ddecfa] rounded-t-2xl flex items-center justify-between px-8 pt-6 pb-4 border-b-2 border-[#efe6c8] shadow-md">
          <div className="flex items-center gap-3">
            <FaRegEdit className="text-[#f3b409] text-3xl drop-shadow" />
            <span className="font-black text-2xl tracking-wide text-[#99790a] drop-shadow">
              Edit Transaction
              <span className="ml-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-[#fef8ec] text-[#a58527] text-base font-bold border border-[#eee4bc] shadow-sm">
                #{transaction.id}
              </span>
            </span>
          </div>
          <button
            className="text-3xl leading-none text-[#868cab] hover:text-[#222] px-4 py-1.5 rounded-full transition-all duration-150 bg-transparent hover:bg-[#eee] focus:ring-2 focus:ring-[#bba81d] outline-none"
            aria-label="Close"
            onClick={onClose}
            disabled={isSaving}
            type="button"
          >
            ×
          </button>
        </div>
        {/* Form */}
        <form
          className="grid grid-cols-2 gap-x-8 gap-y-6 px-12 py-10"
          onSubmit={async (e) => {
            e.preventDefault();
            setIsSaving(true);
            setError(null);
            try {
              await onSave(formdata as Transaction);
              setIsSaving(false);
            } catch (err: any) {
              setIsSaving(false);
              setError(
                err?.response?.data?.error ||
                err?.message ||
                "Failed to save changes. Please try again."
              );
            }
          }}
        >
          <div>
            <label className="block text-[1.07rem] text-[#263349] font-semibold mb-2">
              Type
            </label>
            <select
              className="border-2 border-[#dde2eb] rounded-lg px-3 py-2 text-base w-full focus:ring-2 focus:ring-[#bbdbfd] transition"
              value={formdata.type}
              onChange={(e) => handleChange("type", e.target.value)}
              disabled
            >
              <option value="buy">Buy</option>
              <option value="sell">Sell</option>
              <option value="withdraw">Withdraw</option>
            </select>
          </div>
          <div>
            <label className="block text-[1.07rem] text-[#263349] font-semibold mb-2">
              Date
            </label>
            <input
              className="border-2 border-[#dde2eb] rounded-lg px-3 py-2 text-base w-full focus:ring-2 focus:ring-[#bbdbfd] transition"
              type="date"
              value={formdata.date}
              onChange={(e) => handleChange("date", e.target.value)}
              disabled={isSaving}
            />
          </div>
          <div>
            <label className="block text-[1.07rem] text-[#263349] font-semibold mb-2">
              Stock
            </label>
            <select
              className="border-2 border-[#dde2eb] rounded-lg px-3 py-2 text-base w-full focus:ring-2 focus:ring-[#bbdbfd] transition"
              value={formdata.stock_id ?? ""}
              onChange={(e) => handleChange("stock_id", e.target.value)}
              disabled={isSaving}
            >
              <option value="">-- Select --</option>
              {stockNames.map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
              {formdata.stock_id &&
                typeof formdata.stock_id === "string" &&
                !stockNames.includes(formdata.stock_id) && (
                  <option value={formdata.stock_id}>{formdata.stock_id}</option>
                )}
            </select>
          </div>
          <div>
            <label className="block text-[1.07rem] text-[#263349] font-semibold mb-2">
              Category
            </label>
            <select
              className="border-2 border-[#dde2eb] rounded-lg px-3 py-2 text-base w-full focus:ring-2 focus:ring-[#bbdbfd] transition"
              value={formdata.category_id ?? ""}
              onChange={(e) => handleChange("category_id", e.target.value)}
              disabled={isSaving}
            >
              <option value="">-- Select --</option>
              {allCategories.map((c) => (
                <option key={c.category_name} value={c.category_name}>
                  {typeof c.category_name === "object" ? "-" : c.category_name}
                </option>
              ))}
              {formdata.category_id &&
                typeof formdata.category_id === "string" &&
                !allCategories.find(
                  (c) => c.category_name === formdata.category_id
                ) && (
                  <option value={formdata.category_id}>
                    {formdata.category_id}
                  </option>
                )}
            </select>
          </div>
          <div>
            <label className="block text-[1.07rem] text-[#263349] font-semibold mb-2">
              Party Name
            </label>
            <select
              className="border-2 border-[#dde2eb] rounded-lg px-3 py-2 text-base w-full focus:ring-2 focus:ring-[#bbdbfd] transition"
              value={formdata.party_id ?? ""}
              onChange={(e) => handleChange("party_id", e.target.value)}
              disabled={isSaving}
            >
              <option value="">-- Select --</option>
              {allParties.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
              {formdata.party_id &&
                !allParties.find(
                  (p) => String(p.id) === String(formdata.party_id)
                ) && (
                  <option value={formdata.party_id}>
                    {transaction.party_id || "Loading Party..."}
                  </option>
                )}
            </select>
          </div>
          <div>
            <label className="block text-[1.07rem] text-[#263349] font-semibold mb-2">
              Qty
            </label>
            <input
              className="border-2 border-[#dde2eb] rounded-lg px-3 py-2 text-base w-full focus:ring-2 focus:ring-[#bbdbfd] transition"
              type="number"
              value={formdata.qty}
              onChange={(e) => handleChange("qty", e.target.value)}
              disabled={isSaving}
              min="0"
            />
          </div>
          <div>
            <label className="block text-[1.07rem] text-[#263349] font-semibold mb-2">
              Unit
            </label>
            <input
              className="border-2 border-[#dde2eb] rounded-lg px-3 py-2 text-base w-full focus:ring-2 focus:ring-[#bbdbfd] transition"
              value={formdata.unit ?? ""}
              onChange={(e) => handleChange("unit", e.target.value)}
              disabled={isSaving}
              maxLength={10}
            />
          </div>
          <div>
            <label className="block text-[1.07rem] text-[#263349] font-semibold mb-2">
              Amount (₹)
            </label>
            <input
              className="border-2 border-[#dde2eb] rounded-lg px-3 py-2 text-base w-full focus:ring-2 focus:ring-[#bbdbfd] transition"
              type="number"
              value={formdata.amount}
              onChange={(e) => handleChange("amount", e.target.value)}
              disabled={isSaving}
              min="0"
            />
          </div>
          {formdata.type === "sell" && (
            <div>
              <label className="block text-[1.07rem] text-[#263349] font-semibold mb-2">
                Indirect Charges
              </label>
              <input
                className="border-2 border-[#dde2eb] rounded-lg px-3 py-2 text-base w-full focus:ring-2 focus:ring-[#bbdbfd] transition"
                type="number"
                value={formdata.indirect_charges ?? ""}
                onChange={(e) =>
                  handleChange("indirect_charges", e.target.value)
                }
                disabled={isSaving}
                min="0"
              />
            </div>
          )}
          <div className="col-span-2">
            <label className="block text-[1.07rem] text-[#263349] font-semibold mb-2">
              Remark
            </label>
            <input
              className="border-2 border-[#dde2eb] rounded-lg px-3 py-2 text-base w-full focus:ring-2 focus:ring-[#bbdbfd] transition"
              value={formdata.remark ?? ""}
              onChange={(e) => handleChange("remark", e.target.value)}
              disabled={isSaving}
              maxLength={200}
            />
          </div>
          {error && (
            <div className="col-span-2 text-red-600 text-base font-bold mt-2 pl-1">
              {error}
            </div>
          )}
          <div className="col-span-2 flex gap-5 mt-8 mb-2 justify-end">
            <button
              type="button"
              className="px-6 py-2 rounded-lg bg-white font-bold text-[#707eb3] text-base border-2 border-[#c4d5fd] hover:bg-[#f6f8fa] shadow-md transition-all duration-100"
              onClick={onClose}
              disabled={isSaving}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-6 py-2 rounded-lg bg-gradient-to-tr from-[#fae29e] to-[#f5c766] font-bold text-[#6c4f0b] text-base border-2 border-[#fdeec2] hover:bg-[#f7dfa4] shadow-lg transition-all duration-100"
              disabled={isSaving}
            >
              {isSaving ? (
                <span className="flex items-center gap-2">
                  <span className="animate-spin w-4 h-4 border-2 border-yellow-400 border-t-transparent rounded-full inline-block"></span>
                  Saving...
                </span>
              ) : (
                "Save"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}


const QUICK_FILTERS: { key: string; label: string }[] = [
  { key: "last7", label: "Last 7 Days" },
  { key: "thisMonth", label: "This Month" },
  { key: "lastMonth", label: "Last Month" },
  { key: "ytd", label: "YTD" },
  { key: "withdraw", label: "Withdrawals" },
  { key: "highValue", label: "High Value (>₹1L)" },
];

function applyQuickFilter(key: string): Partial<FilterState> {
  const today = new Date();
  if (key === "last7") {
    const to = today.toISOString().slice(0, 10);
    const from = new Date(today.setDate(today.getDate() - 6)).toISOString().slice(0, 10);
    return { from, to };
  } else if (key === "thisMonth") {
    const from = new Date(today.getFullYear(), today.getMonth(), 1)
      .toISOString()
      .slice(0, 10);
    const to = new Date(today.getFullYear(), today.getMonth() + 1, 0)
      .toISOString()
      .slice(0, 10);
    return { from, to };
  } else if (key === "lastMonth") {
    const from = new Date(today.getFullYear(), today.getMonth() - 1, 1)
      .toISOString()
      .slice(0, 10);
    const to = new Date(today.getFullYear(), today.getMonth(), 0)
      .toISOString()
      .slice(0, 10);
    return { from, to };
  } else if (key === "ytd") {
    const from = new Date(today.getFullYear(), 0, 1).toISOString().slice(0, 10);
    return { from };
  } else if (key === "withdraw") {
    return { type: "withdraw" };
  } else if (key === "highValue") {
    return { minAmount: String(1e5) };
  }
  return {};
}

// --- Modified AdvancedFilters for "party" via URL AND reflect in /id param ---
function AdvancedFilters({
  filters,
  setFilters,
  allParties,
  allStocks,
  allCategories,
}: {
  filters: FilterState;
  setFilters: React.Dispatch<React.SetStateAction<FilterState>>;
  allParties: Party[];
  allStocks: Stock[];
  allCategories: Category[];
}) {
  // For programmatic navigation (add/remove party param AND id param)
  const location = useLocation();
  const navigate = useNavigate();

  function updateQueryParamParty(partyId: string) {
    const searchParams = new URLSearchParams(location.search);
    if (partyId && partyId !== "") {
      searchParams.set("party", partyId);
    } else {
      searchParams.delete("party");
    }

    // Also set the /id segment in the route params if present in path.
    // E.g. /userhistory or /userhistory/:id, update id as necessary.
    // We assume this route is /userhistory[/:id], so update last segment if number.
    let updatedPath = location.pathname;
    const pathParts = updatedPath.split("/");
    // If path ends with a number, treat as id and update.
    if (partyId && partyId !== "") {
      if (!isNaN(Number(pathParts[pathParts.length - 1]))) {
        pathParts[pathParts.length - 1] = partyId;
        updatedPath = pathParts.join("/");
      } else if (pathParts[pathParts.length - 1] !== partyId) {
        // Not ending in id, so append
        // if (updatedPath.endsWith("/")) {
        //   updatedPath += partyId;
        // } else {
        //   updatedPath += "/" + partyId;
        // }
      }
    } else {
      // If last part is a number (was previously id), remove it
      if (!isNaN(Number(pathParts[pathParts.length - 1]))) {
        updatedPath = pathParts.slice(0, -1).join("/") || "/";
      }
    }

    navigate(
      {
        pathname: updatedPath,
        search: searchParams.toString(),
      },
      { replace: true }
    );
  }

  const handleChange = (field: keyof FilterState, value: any) => {
    if (field === "party_id") {
      setFilters((prev) => ({
        ...prev,
        [field]: value === "All" ? undefined : value,
      }));
      updateQueryParamParty(value);
    } else {
      setFilters((prev) => ({
        ...prev,
        [field]: value === "All" ? undefined : value,
      }));
    }
  };

  return (
    <div className="bg-[#e5f2fb] border border-[#d5e8fc] rounded-md p-4 flex flex-wrap gap-4 items-end mb-4">
      <div>
        <label className="block text-xs font-semibold text-[#2b415a] mb-1">Party</label>
        <select
          className="px-2 py-1.5 rounded border text-sm min-w-[120px] focus:ring"
          value={filters.party_id ?? ""}
          onChange={(e) => handleChange("party_id", e.target.value)}
        >
          <option value="">All Parties</option>
          {allParties.map((p) => (
            <option key={p.id} value={p.id}>
              {typeof p.name === "object" ? "-" : p.name ?? p.id}
            </option>
          ))}
        </select>
      </div>
      <div>
        <label className="block text-xs font-semibold text-[#2b415a] mb-1">Type</label>
        <select
          className="px-2 py-1.5 rounded border text-sm min-w-[110px] focus:ring"
          value={filters.type ?? "All"}
          onChange={(e) => handleChange("type", e.target.value)}
        >
          <option value="All">All Types</option>
          <option value="buy">Buy</option>
          <option value="withdraw">Withdraw</option>
          <option value="sell">Sell</option>
        </select>
      </div>
     
      <div>
        <label className="block text-xs font-semibold text-[#2b415a] mb-1">Category</label>
        <select
          className="px-2 py-1.5 rounded border text-sm min-w-[120px] focus:ring"
          value={filters.category_id ?? ""}
          onChange={(e) => handleChange("category_id", e.target.value)}
        >
          <option value="">All Categories</option>
          {allCategories.map((c) => (
            <option key={c.id} value={c.id}>
              {typeof c.category_name === "object"
                ? "-"
                : c.category_name || (typeof c.id === "object" ? "-" : c.id)}
            </option>
          ))}
        </select>
      </div>
      <div>
        <label className="block text-xs font-semibold text-[#2b415a] mb-1">From Date</label>
        <input
          type="date"
          className="px-2 py-1.5 rounded border text-sm min-w-[120px] focus:ring"
          value={filters.from ?? ""}
          onChange={(e) => handleChange("from", e.target.value)}
        />
      </div>
      <div>
        <label className="block text-xs font-semibold text-[#2b415a] mb-1">To Date</label>
        <input
          type="date"
          className="px-2 py-1.5 rounded border text-sm min-w-[120px] focus:ring"
          value={filters.to ?? ""}
          onChange={(e) => handleChange("to", e.target.value)}
        />
      </div>
      <div>
        <label className="block text-xs font-semibold text-[#2b415a] mb-1">Search</label>
        <input
          type="text"
          placeholder="Search by id/remark"
          className="px-2 py-1.5 rounded border text-sm min-w-[130px] focus:ring"
          value={filters.search ?? ""}
          onChange={(e) => handleChange("search", e.target.value)}
        />
      </div>
      <div>
        <label className="block text-xs font-semibold text-[#2b415a] mb-1">Min Amt</label>
        <input
          type="number"
          className="px-2 py-1.5 rounded border text-sm min-w-[90px] focus:ring"
          value={filters.minAmount ?? ""}
          onChange={(e) => handleChange("minAmount", e.target.value)}
        />
      </div>
      <div>
        <label className="block text-xs font-semibold text-[#2b415a] mb-1">Max Amt</label>
        <input
          type="number"
          className="px-2 py-1.5 rounded border text-sm min-w-[90px] focus:ring"
          value={filters.maxAmount ?? ""}
          onChange={(e) => handleChange("maxAmount", e.target.value)}
        />
      </div>
      <div>
        <button
          className="mt-4 px-3 py-1.5 rounded bg-[#f2e3fe] text-[#6c46b7] font-bold text-xs border border-[#e2d6fb] shadow hover:bg-[#e9fafe] transition"
          onClick={() => {
            setFilters({});
            updateQueryParamParty('');
          }}
          type="button"
        >
          Reset Filters
        </button>
      </div>
    </div>
  );
}

function getTxnTypeInfo(type: TransactionType) {
  if (type === "buy")
    return {
      color: "bg-[#e6fbe9] text-[#23be5a]",
      icon: <BsArrowDownCircle className="text-[#47bc6b] text-2xl" />,
      label: "Buy / Invest",
    };
  if (type === "sell")
    return {
      color: "bg-[#ffe3ea] text-[#e23a4e]",
      icon: <BsArrowUpCircle className="text-[#ef5398] text-2xl" />,
      label: "Sell",
    };
  return {
    color: "bg-[#fefee8] text-[#c6a01d]",
    icon: <HiOutlineCurrencyRupee className="text-[#d6b01c] text-2xl" />,
    label: "Withdraw",
  };
}

function PhonePeSidePanel({
  transactions,
  allStocks,
  allParties,
  categories,
  onSelect,
  selectedTxnId,
}: {
  transactions: Transaction[];
  allStocks: Stock[];
  allParties: Party[];
  categories: Category[];
  onSelect: (txn: {
    transaction: Transaction;
    stock: Stock | undefined;
    category: Category | any;
    party: Party | undefined;
  }) => void;
  selectedTxnId: string | number | null;
}) {
  React.useEffect(() => {
    if (!selectedTxnId) return;
    const el = document.getElementById("txn-item-" + selectedTxnId);
    if (el) el.scrollIntoView({ block: "center", behavior: "smooth" });
  }, [selectedTxnId]);

  function findDetails(t: Transaction) {
    const stock = allStocks.find((s) => String(s.id) === String(t.stock_id));
    let category = null;
    if (stock && Array.isArray(stock.categories)) {
      category = stock.categories.find((cat: any) => {
        const catId = typeof cat.id === "object" ? undefined : cat.id;
        const catName =
          typeof cat.category_name === "object"
            ? undefined
            : cat.category_name;
        const catValue = typeof cat === "object" ? undefined : cat;
        return String(catId ?? catName ?? catValue) === String(t.category_id);
      });
    }
    if (!category && t.category_id) {
      category = categories.find(
        (c) =>
          (typeof c.id !== "object" &&
            String(c.id) === String(t.category_id)) ||
          (typeof c.category_name !== "object" &&
            c.category_name === t.category_id)
      );
    }
    const party = allParties.find(
      (p) => String(p.id) === String(t.party_id)
    );
    return { stock, category, party };
  }

  return (
    <aside
      className="fixed top-0 right-0 h-full bg-white border-l z-40 shadow-2xl flex flex-col px-0 pt-3 pb-3 w-[350px] max-w-full min-w-[238px] transition-all duration-300 animate-fade-in overflow-y-auto"
      style={{
        boxShadow: "-2px 0 18px -3px #b0cbf533",
        background:
          "linear-gradient(180deg,#e8f0fe 3%,#fcfcff 90%)",
      }}
    >
      <div className="text-lg px-6 pb-1.5 font-black text-[#25669a] flex gap-2 items-center sticky top-0 bg-white z-10">
        <FaExchangeAlt className="text-[#357fb2]" />
        Transactions ({transactions.length})
      </div>
      <div className="flex-1 overflow-y-auto pr-3 pt-3 pb-3">
        <ul className="flex flex-col gap-2">
          {transactions.map((txn) => {
            const { color, icon, label } = getTxnTypeInfo(txn.type);
            const { stock, category, party } = findDetails(txn);
            return (
              <li
                id={`txn-item-${txn.id}`}
                key={txn.id}
                className={`rounded-xl border border-[#e0e6f9] px-3 py-2 mb-1 cursor-pointer flex gap-2 items-center shadow font-sans transition-all duration-200
                  ${selectedTxnId === txn.id ? "ring-2 ring-[#8edbfc] bg-[#eafdff]" : "hover:bg-[#fbf7fe]"}`}
                onClick={() => onSelect({ transaction: txn, stock, category, party })}
                tabIndex={0}
              >
                <span className={"rounded-xl flex items-center justify-center w-10 h-10 mr-2 font-bold shadow-sm border " + color}>
                  {icon}
                </span>
                <div className="flex-1 flex flex-col gap-[2px]">
                  <div className="text-[16px] font-bold leading-[1.20] select-all text-[#2b4254]">
                    {typeof party?.name === "object" ? "-" : party?.name ?? party?.id ?? "-"}
                  </div>
                  <div className="text-[11.5px] text-[#879ab3] font-semibold tracking-wide">
                    {txn.date} &bull; {label}
                  </div>
                  <div className="flex flex-wrap items-center gap-2 mt-1">
                    <span className="text-xs inline-flex items-center gap-1 px-2 py-0.5 rounded bg-[#d4f7fd]/60 text-[#257a9e]">
                      <BsBoxArrowInDownRight className="inline w-4 h-4" />
                      {typeof stock?.id === "object" ? "-" : stock?.id ?? txn.stock_id ?? "-"}
                    </span>
                    <span className="text-xs inline-flex items-center gap-1 px-2 py-0.5 rounded bg-[#dbe1f5] text-[#6759c9]">
                      {typeof category?.category_name === "object"
                        ? "-"
                        : category?.category_name ||
                          (typeof category?.category === "object"
                            ? "-"
                            : category?.category) ||
                          (typeof category?.stock_name === "object"
                            ? "-"
                            : category?.stock_name) ||
                          (typeof txn.category_id === "object"
                            ? "-"
                            : txn.category_id) ||
                          "-"}
                    </span>
                    <span
                      className={`text-xs inline-flex items-center gap-1 px-2 py-0.5 rounded bg-[#def1e7] text-[#12876a]`}
                    >
                      x{txn.qty} {typeof txn.unit === "object" ? "-" : txn.unit ?? "-"}
                    </span>
                  </div>
                </div>
                <div className="ml-1 text-right flex flex-col justify-between h-full">
                  <span
                    className={
                      "font-bold text-[15px] " +
                      (txn.type === "buy"
                        ? "text-[#12b386]"
                        : txn.type === "sell"
                        ? "text-[#f24464]"
                        : "text-[#ba961b]")
                    }
                  >
                    {formatMoney(txn.amount, { symbol: true })}
                  </span>
                  <span className="text-[9.5px] text-[#a8abb6] font-medium block mt-1 text-right">
                    {txn.remark && typeof txn.remark !== "object" ? txn.remark : ""}
                  </span>
                </div>
              </li>
            );
          })}
          {!transactions.length && (
            <li className="text-center text-[#b3b9cf] font-semibold py-12">No transactions.</li>
          )}
        </ul>
      </div>
      <span className="block px-6 py-3 text-xs text-[#6a8cbc] font-semibold border-t bg-[#e7f7ff]">
        <span className="font-bold text-[#3e7bbd]">Tip:</span> Tap a transaction to view more details quickly.
      </span>
    </aside>
  );
}
function isRecent(dateStr: string | undefined, hours: number = 24): boolean {
  if (!dateStr) return false;
  // Assume dateStr is "YYYY-MM-DD" or ISO-formatted string
  // Always treat time as local date's 00:00 to 23:59 for "today"
  try {
    const datePart = dateStr.length > 10 ? dateStr.slice(0, 10) : dateStr;
    const txnDate = new Date(datePart);
    const now = new Date();
    // Check if it's today
    if (
      txnDate.getFullYear() === now.getFullYear() &&
      txnDate.getMonth() === now.getMonth() &&
      txnDate.getDate() === now.getDate()
    ) {
      return true;
    }
    // Or if in the past N hours (24 by default)
    const diff = now.getTime() - txnDate.getTime();
    return diff < hours * 60 * 60 * 1000 && diff > 0;
  } catch {
    return false;
  }
}
// ========== MAIN COMPONENT ==========

function UserHistory() {
  // ... all state/data code as before ...
  const [allParties, setAllParties] = useState<Party[]>([]);
  const [allStocks, setAllStocks] = useState<Stock[]>([]);
  const [withdrawals, setWithdrawals] = useState<Withdrawal[]>([]);
  const [stockHistory, setStockHistory] = useState<StockHistory[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [sellTxnsRaw, setSellTxnsRaw] = useState<any[]>([]);
  const [filters, setFilters] = useState<FilterState>({});
  const [isLoading, setIsLoading] = useState(true);
  const [sidePanelCollapsed, setSidePanelCollapsed] = useState(true);

  // For inline edit modal
  const [editTransactionModal, setEditTransactionModal] = useState<null | {
    transaction: Transaction,
    stock: Stock | undefined,
    category: Category | any,
    party: Party | undefined
  }>(null);

  const location = useLocation();
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const partyFromUrl = searchParams.get("party");
    let idFromPath: string | undefined = undefined;
    const pathParts = location.pathname.split("/");
    if (pathParts.length > 1) {
      const last = pathParts[pathParts.length - 1];
      if (last && !isNaN(Number(last))) {
        idFromPath = last;
      }
    }
    let newPartyId: string | undefined = partyFromUrl || idFromPath;
    setFilters(prev => {
      if (newPartyId && String(prev.party_id) !== String(newPartyId)) {
        return { ...prev, party_id: newPartyId };
      } else if (!newPartyId && !!prev.party_id) {
        let cp = { ...prev };
        delete cp.party_id;
        return cp;
      }
      return prev;
    });
  }, [location.search, location.pathname]);

  useEffect(() => {
    if (window.location.hash.startsWith("#q=")) {
      const quickKey = decodeURIComponent(window.location.hash.slice(3));
      if (QUICK_FILTERS.find(q => q.key === quickKey)) {
        setFilters((prev) => ({ ...prev, quick: quickKey, ...applyQuickFilter(quickKey) }));
      }
    }
  }, []);

  const [selectedTxnPanel, setSelectedTxnPanel] = useState<string | number | null>(null);
  const [selectedTransaction, setSelectedTransaction] = useState<{
    transaction: Transaction;
    stock: Stock | undefined;
    category: Category | any;
    party: Party | undefined;
  } | null>(null);

  // fetchAll remains unchanged, but abstract function for reuse after update
  async function fetchAllData() {
    setIsLoading(true);
    try {
      const partiesRes = await axios.get(
        `${process.env.REACT_APP_API_URL}/auth/addparty`
      );
      setAllParties(partiesRes.data || []);

      const stocksRes = await axios.get(
        `${process.env.REACT_APP_API_URL}/auth/addstocks`
      );
      let stocksArr = Array.isArray(stocksRes.data) ? stocksRes.data : [];
      stocksArr = stocksArr.map((stock: any) => ({
        ...stock,
        categories: (() => {
          try {
            if (Array.isArray(stock.categories)) return stock.categories;
            if (typeof stock.categories === "string" && stock.categories.length > 0)
              return JSON.parse(stock.categories);
            return [];
          } catch {
            return [];
          }
        })(),
      }));
      setAllStocks(stocksArr);

      const withdrawalRes = await axios.get(
        `${process.env.REACT_APP_API_URL}/auth/withdrawals`
      );
      setWithdrawals(withdrawalRes.data || []);

      const stockHistoryRes = await axios.get(
        `${process.env.REACT_APP_API_URL}/auth/updatestock`
      );
      setStockHistory(stockHistoryRes.data || []);

      const categoriesRes = await axios.get(
        `${process.env.REACT_APP_API_URL}/auth/categories`
      );
      setCategories(categoriesRes.data || []);

      const sellStocksRes = await axios.get(
        `https://stockaadvikbackend.aadviktech.com/auth/sellstocks`
      );
      setSellTxnsRaw(Array.isArray(sellStocksRes.data) ? sellStocksRes.data : []);
      setIsLoading(false);
    } catch (e) {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    fetchAllData();
  }, []);

  function handleQuickFilter(key: string) {
    setFilters((prev) => ({
      ...prev,
      ...applyQuickFilter(key),
      quick: key,
    }));
    window.location.hash = "#q=" + encodeURIComponent(key);
  }
  function clearQuickFilter() {
    setFilters((prev) => {
      let { quick, ...rest } = prev;
      return { ...rest };
    });
    window.location.hash = "";
  }

  // All calculations are using live (real) data
  let buyTransactions: Transaction[] = [];
  allStocks.forEach((stock) => {
    if (Array.isArray(stock.categories) && stock.categories.length > 0) {
      stock.categories.forEach((cat: any) => {
        // Always treat cat.unit as the quantity (string to number)
        const qty = 
          typeof cat.unit === "number" 
            ? cat.unit 
            : typeof cat.unit === "string"
            ? Number(cat.unit)
            : 0;
      //  console.log("qty from total bought ", qty);
        buyTransactions.push({
          id: `buy_${stock.id}_${cat.id ?? cat.category_name}`,
          party_id: stock.party_id ?? "",
          date: stock.date?.slice(0, 10) || "",
          investment_type: cat.investment_type || "structure",
          type: "buy",
          stock_id: stock.id,
          category_id: cat.id ?? cat.category_name ?? cat,
          qty: isNaN(qty) ? 1 : qty,
          unit:
            typeof cat.unit === "object" || cat.unit === undefined
              ? "-"
              : String(cat.unit),
          amount:
            typeof cat.total_amount === "object"
              ? 0
              : (Number(cat.total_amount) || 0) + (cat.indirect_charges != null ? Number(cat.indirect_charges) || 0 : 0),
         
          remark: "Stock added",
        });
      });
    }
  });

  let withdrawalTransactions: Transaction[] = [];
withdrawals.forEach((wd) => {
  let cats: any[] = [];
  try {
    // Parsing Phase 1: Removes the outer escaped string
    let firstPass = JSON.parse(wd.catrgories || "[]");
    
    // Parsing Phase 2: If the result is an array containing a string, parse that string
    if (Array.isArray(firstPass) && typeof firstPass[0] === "string") {
      cats = JSON.parse(firstPass[0]);
    } else {
      cats = firstPass;
    }
  } catch (e) {
    console.error("Error parsing withdrawal categories", e);
    cats = [];
  }

  if (cats.length === 0) {
    withdrawalTransactions.push({
      id: `withdraw_${wd.id}`,
      party_id: wd.party_id,
      date: wd.date?.slice(0, 10) || "",
      type: "withdraw",
     
      stock_id: undefined,
      category_id: undefined,
      qty: 1,
      unit: "-",
      amount: Number(wd.amount) || 0,
      remark: "Withdrawal",
    });
    return;
  }

  cats.forEach((c: any, i: number) => {
    // Extract name and ID from the parsed object
    const stockName = c.stockName || c.stock_name;
    const categoryName = c.category || c.category_name;
    
    withdrawalTransactions.push({
      id: `withdraw_${wd.id}_${i}`,
      party_id: wd.party_id,
      date: wd.date?.slice(0, 10) || "",
      type: "withdraw",
      investment_type: "structure",
      // CHANGE: Use stockName as the identifier to match Sells
      stock_id: stockName, 
      category_id: categoryName,
      qty: 1,
      unit: "-",
      // If multiple categories exist in one withdrawal, split the amount
      amount: Number(c.amount || wd.amount) / (cats.length || 1),
      remark: "Withdrawal",
    });
  });
});

  const sellTransactions: Transaction[] = useMemo(() => {
  return (sellTxnsRaw || []).map((sell) => ({
    id: `sell_${sell.id}`,
    party_id: sell.party_id,
    date: sell.date?.slice(0, 10) ?? "",
    type: "sell",
    investment_type: sell.investment_type || "structure",
    // CHANGE: Use the name field from your sell API instead of a numeric ID
    stock_id: sell.stock_name || sell.stock_id, 
    category_id: sell.category,
    qty: typeof sell.sell_units === "string" ? parseFloat(sell.sell_units) : (sell.sell_units ?? 0),
    unit: sell.unit || "units",
    amount: typeof sell.sell_amount === "string" ? parseFloat(sell.sell_amount) : (sell.sell_amount ?? 0),
    remark: sell.remark ?? "Sell",
    indirect_charges: sell.indirect_charges !== undefined ? Number(sell.indirect_charges) : undefined,
  }));
}, [sellTxnsRaw]);

  let allTransactionsRaw: Transaction[] = [
    ...buyTransactions,
    ...withdrawalTransactions,
    ...sellTransactions,
  ].map((t) => ({
    ...t,
    unit: typeof t.unit === "object" ? "-" : t.unit || "-",
  }));

  //console.log("allTransactionsRaw ",allTransactionsRaw);
  // FILTERING LOGIC (uses real data)
  const filteredTransactions = useMemo(() => {
    return allTransactionsRaw
      .filter((t) => {
        if (filters.party_id && String(t.party_id) !== String(filters.party_id))
          return false;
        if (filters.from && t.date < filters.from) return false;
        if (filters.to && t.date > filters.to) return false;
        if (filters.type && filters.type !== "All" && t.type !== filters.type)
          return false;
        if (
          filters.stock_id &&
          filters.stock_id !== "All" &&
          String(t.stock_id) !== String(filters.stock_id)
        )
          return false;
        if (
          filters.category_id &&
          filters.category_id !== "All" &&
          String(t.category_id) !== String(filters.category_id)
        )
          return false;
        if (
          filters.minAmount &&
          (isNaN(Number(t.amount)) ||
            Number(t.amount) < Number(filters.minAmount))
        )
          return false;
        if (
          filters.maxAmount &&
          (isNaN(Number(t.amount)) ||
            Number(t.amount) > Number(filters.maxAmount))
        )
          return false;
        if (
          filters.search &&
          !(
            String(t.id).includes(filters.search) ||
            String(t.remark || "")
              .toLowerCase()
              .includes(filters.search.toLowerCase()) ||
            String(t.amount).includes(filters.search) ||
            String(t.unit).includes(filters.search)
          )
        )
          return false;
        return true;
      })
      .sort((a, b) =>
        a.date === b.date
          ? String(a.id) < String(b.id)
            ? -1
            : 1
          : new Date(a.date).getTime() - new Date(b.date).getTime()
      );
  }, [
    JSON.stringify(allTransactionsRaw),
    filters.from,
    filters.to,
    filters.type,
    filters.stock_id,
    filters.category_id,
    filters.party_id,
    filters.minAmount,
    filters.maxAmount,
    filters.search,
  ]);

function findStockAndCategory(
  stock_identifier: string | number | undefined, // This is now a name for sells
  category_id: string | number | undefined
) {
  // Find stock where ANY category inside it matches the name OR the stock ID matches
  const stock = allStocks.find((s) => 
    String(s.id) === String(stock_identifier) || 
    s.categories.some(cat => cat.stock_name === stock_identifier)
  );

  let category = null;
  if (stock && Array.isArray(stock.categories)) {
    category = stock.categories.find((cat: any) => {
      // Check for ID match or name match (useful for sells)
      const isMatch = String(cat.id ?? cat.category_name) === String(category_id) || 
                      cat.stock_name === stock_identifier;
      return isMatch;
    });
  }

  // Fallback if not found in stock categories
  if (!category && category_id) {
    category = categories.find(
      (c) => String(c.id) === String(category_id) || c.category_name === category_id
    );
  }
  
  // Final fallback to display the identifier
  if (!category && category_id) {
    category = { category_name: category_id };
  }
  
  return { stock, category };
}

  // Live value calculation


  type AssetHolding = {
    stock_id: string | number | undefined;
    category_id: string | number | undefined;
    party_id: number | string;
    stock?: Stock;
    investment_type:string | undefined;
    category?: any;
    total_bought: number;
    total_withdraw: number;
    total_sold: number;
    remaining_qty: number;
    unit: string;
    current_price: number;
    holding_value: number;
    history: Transaction[];
    avg_buy_price?: number;
  };

  const assetHoldingsMap: Map<string, AssetHolding> = new Map();

  
const processedHoldings = useMemo(() => {
  const holdingsMap = new Map<string, AssetHolding>();

  // STEP 1: Aggregate Quantities (Bought, Sold, Remaining)
  filteredTransactions.forEach((t) => {
    const { stock, category } = findStockAndCategory(t.stock_id, t.category_id);
    
    // Normalize Key to match Buys (IDs) and Sells (Names)
    const stockName = category?.stock_name || category?.stockName || stock?.id || String(t.stock_id || "-");
    const catName = category?.category_name || category?.category || String(t.category_id || "-");
    const key = `${stockName}_${catName}`.toLowerCase().trim();

    if (!holdingsMap.has(key)) {
      holdingsMap.set(key, {
        stock_id: t.stock_id,
        category_id: t.category_id,
        party_id: t.party_id,
        investment_type: t.investment_type,
        stock,
        category,
        total_bought: 0,
        total_withdraw: 0,
        total_sold: 0,
        remaining_qty: 0,
        unit: t.unit || "-",
        current_price: 0,
        holding_value: 0,
        history: [],
      });
    }

    const record = holdingsMap.get(key)!;
    const qty = parseFloat(String(t.qty)) || 0;

    if (t.type === "buy") {
      record.total_bought += qty;
      record.remaining_qty += qty;
    } else if (t.type === "sell") {
      record.total_sold += qty;
      record.remaining_qty -= qty;
    } else if (t.type === "withdraw") {
      record.total_withdraw += qty;
      record.remaining_qty -= qty;
    }
    record.history.push(t);
  });

  // STEP 2: Calculate Prices and Values
  holdingsMap.forEach((record) => {
    // A. Current Price Lookup (from history table)
    const latestPriceRec = stockHistory
      .filter(h => String(h.stock_id) === String(record.stock_id))
      .sort((a, b) => new Date(b.current_price_date).getTime() - new Date(a.current_price_date).getTime())[0];

    let cPrice = 0;
    if (latestPriceRec) {
      cPrice = parseFloat(String(latestPriceRec.current_price)) || 0;
    } else {
      // Fallback: If no history, use the price from the category object
      cPrice = record.category?.total_amount / (record.category?.unit || 1) || 0;
    }

    // B. Average Buy Price Calculation
    const buys = record.history.filter(tr => tr.type === "buy");
    const totalSpent = buys.reduce((sum, tr) => sum + (Number(tr.amount) || 0), 0);
    const totalBoughtQty = buys.reduce((sum, tr) => sum + (Number(tr.qty) || 0), 0);
    
    record.current_price = cPrice;
    record.avg_buy_price = totalBoughtQty > 0 ? totalSpent / totalBoughtQty : 0;
    
    // C. Total Holding Value
    record.holding_value = Math.max(0, record.remaining_qty) * cPrice;
  });

  return holdingsMap;
}, [filteredTransactions, stockHistory, allStocks]);












  const stats = useMemo(() => {
    let invested = 0;
    let withdrawn = 0;
    let sold = 0;
    let balance = 0;
  
    // Global sums from all filtered transactions
    filteredTransactions.forEach((t) => {
      const amt = parseFloat(String(t.amount)) || 0;
      if (t.type === "buy") invested += amt;
      else if (t.type === "withdraw") withdrawn += amt;
      else if (t.type === "sell") sold += amt;
    });
  
    // Balance comes only from assets with positive remaining quantity
    processedHoldings.forEach((record) => {
      if (record.holding_value > 0) {
        balance += record.holding_value;
      }
    });
  
    return { invested, withdrawn, sold, balance };
  }, [filteredTransactions, processedHoldings]);
 filteredTransactions.forEach((t) => {
  const { stock, category } = findStockAndCategory(t.stock_id, t.category_id);
  
  // CHANGE: Generate key based on Stock Name + Category Name for consistency
  const stockNameKey = category?.stock_name || category?.stockName|| stock?.id || t.stock_id || "-";
  const catNameKey = category?.category_name || t.category_id || "-";
  const key = `${stockNameKey}_${catNameKey}`;

  if (!assetHoldingsMap.has(key)) {
    assetHoldingsMap.set(key, {
      stock_id: t.stock_id,
      category_id: t.category_id,
      party_id: t.party_id,
      investment_type:t.investment_type,
      stock,
      category,
      total_bought: 0,
      total_withdraw: 0,
      total_sold: 0,
      remaining_qty: 0,
      unit: typeof t.unit === "object" ? "-" : t.unit || "-",
      current_price: 0,
      holding_value: 0,
      history: [],
    });
  }
  
  const record = assetHoldingsMap.get(key)!;
  // ... rest of logic for buy/sell/withdraw qty remain same ...
});
//  console.log("assetHoldingsMap ,",assetHoldingsMap);
  assetHoldingsMap.forEach((record) => {
    let priceRec = stockHistory
      .filter(
        (r) =>
          String(r.stock_id) === String(record.stock_id) &&
          String(r.cat_id ?? "") === String(record.category_id ?? "")
      )
      .sort(
        (a, b) =>
          new Date(b.current_price_date).getTime() -
          new Date(a.current_price_date).getTime()
      )[0];
    let cprice = 0;

    if (priceRec && priceRec.current_price) {
      cprice =
        typeof priceRec.current_price === "string"
          ? parseFloat(priceRec.current_price)
          : priceRec.current_price;
    } else if (
      record.category &&
      typeof record.category.total_amount !== "undefined" &&
      typeof record.category.unit !== "undefined" &&
      typeof record.category.total_amount !== "object" &&
      typeof record.category.unit !== "object"
    ) {

      cprice =
        Number(record.category.total_amount) /
        (Number(record.category.unit) || 1);
    }
    record.current_price = cprice || 0;
    record.holding_value = (record.remaining_qty || 0) * (cprice || 0);
    const buys = record.history.filter(tr => tr.type === "buy");
    const totalBuysValue = buys.reduce((sum, tr) => sum + (tr.amount ?? 0), 0);
   
    const totalQty = buys.reduce((sum, tr) => sum + (tr.qty ?? 0), 0);
    record.avg_buy_price = totalQty ? totalBuysValue / totalQty : 0;
    // if (record.holding_value > 0) totalBalance += record.holding_value;
  });

  const percentReturn = stats.invested
    ? (((stats.balance + stats.withdrawn + stats.sold - stats.invested) / stats.invested) * 100).toFixed(2)
    : "0.00";
    const statDetails = [
      {
        icon: <IoWalletSharp className="text-[#F49A40] text-2xl" />,
        label: "Total Invested",
        value: stats.invested, // Change from totalInvested
        color: "#fffee0",
        sub: "All buy amount.",
      },
      {
        icon: <FaRegArrowAltCircleDown className="text-[#29b87f] text-2xl" />,
        label: "Withdrawn",
        value: stats.withdrawn, // Change from totalWithdrawn
        color: "#e7fbe8",
        sub: "All withdrawals.",
      },
      {
        icon: <FaRegArrowAltCircleUp className="text-[#ed0e25] text-2xl" />,
        label: "Amount Sold",
        value: stats.sold, // Change from totalSell
        color: "#ffe3e7",
        sub: "Total sell value.",
      },
      {
        icon: <GiWallet className="text-[#319F68] text-2xl" />,
        label: "Current Value",
        value: stats.balance, // Change from totalBalance
        color: "#e1efed",
        sub: "Current value of holdings.",
      },
      // ... ROI calculation using stats.invested, stats.balance, etc.
    ];

  function printPage() {
    window.print();
  }
  function getYearlySummary() {
    const summary: { [year: string]: number } = {};
    filteredTransactions.forEach(t => {
      const year = t.date?.slice(0, 4) ?? "";
      if (year) summary[year] = (summary[year] || 0) + (t.amount ?? 0);
    });
    return summary;
  }

  // Bar Chart (holdings by stock), real data from assetHoldingsMap
  const barData = {
    labels: Array.from(processedHoldings.values()).map((a) => {
      const unit = a.unit == null || a.unit === undefined ? "-" : a.unit;
      const stockName =
        !a.stock?.categories?.length ||
        a.stock.categories[0].stock_name == null ||
        typeof a.stock.categories[0].stock_name === 'undefined'
          ? "-"
          : a.stock.categories[0].stock_name;
      return `${unit} - ${stockName}`;
    }),
    datasets: [
      {
        label: "Holding Value",
        data: Array.from(processedHoldings.values()).map((a) => a.holding_value),
        backgroundColor: "#74C0FC",
      },
    ],
  };

  const [visibleCols, setVisibleCols] = useState({
    date: true,
    stock: true,
    category: true,
    party: true,
    type: true,
    investment_type: true, // Add this
    qty: true,
    unit: true,
    amount: true,
    remark: true,
    avg_buy_price: false,
  });
  const tableSum = useMemo(() => {
    return filteredTransactions.reduce(
      (acc, curr) => {
        acc.qty += curr.qty;
        acc.amount += curr.amount ?? 0;
        return acc;
      },
      { qty: 0, amount: 0 }
    );
  }, [filteredTransactions]);


  const recentTxnKeys = useMemo(() => {
    return new Set(
      filteredTransactions
        .filter(tr => isRecent(tr.date))
        .map(tr => `${tr.stock_id || "-"}_${tr.category_id || "-"}`)
    );
  }, [filteredTransactions]);


  // Handler for edit button -- NEW: open inline modal
  function handleEditTransaction(t: Transaction) {
    const { stock, category } = findStockAndCategory(t.stock_id, t.category_id);
    const party = allParties.find((p) => String(p.id) === String(t.party_id));
    setEditTransactionModal({ transaction: t, stock, category, party });
  }
  async function handleDeleteTransaction(id: any) {
    if (!id) return;
    // id is in format: buy_123, sell_56, withdraw_89 etc.
    const [tag, rawId] = String(id).split('_');
    if (!tag || !rawId) {
      alert('Invalid transaction ID format.');
      return;
    }

    let url = '';
    let isSuccess = false;

    try {
      if (tag === 'buy') {
        // For buy, DELETE from /addstocks with { id: rawId } in the body (as per API)
        // DELETE /addstocks expects payload { id }
        await axios.delete(`${process.env.REACT_APP_API_URL}/auth/addstocks`, {
          data: { id: rawId },
        });
        isSuccess = true;
      } else if (tag === 'sell') {
        // For sell, DELETE from /sellstocks/:id
        await axios.delete(`${process.env.REACT_APP_API_URL}/auth/sellstocks/${rawId}`);
        isSuccess = true;
      } else if (tag === 'withdraw') {
        // For withdraw, DELETE from /withdrawals/:id
        await axios.delete(`${process.env.REACT_APP_API_URL}/auth/withdrawals/${rawId}`);
        isSuccess = true;
      } else {
        alert("Unknown transaction type.");
      }
      if (isSuccess) {
        alert("Transaction deleted successfully.");
        // Refresh data (re-fetch)
        await fetchAllData();
        // Optionally also update UI, filtered transactions etc.
      }
    } catch (err: any) {
      console.error("Failed to delete transaction:", err);
      alert(
        err?.response?.data?.message ||
        err?.message ||
        "Failed to delete transaction."
      );
    }
  }

  // Handler for save in popup (update in database via API), now fetches all fresh data, then determines just the updated data to update
  async function handleSaveEditedTransaction(edited: Transaction) {
    setIsLoading(true);

    try {
      // Always get latest/fresh complete data before processing update (for consistency & concurrency safe)
      await fetchAllData();

      // After fetching, now get the correct record to update & apply changes
      let updateRes;
      if (String(edited.id).startsWith("buy_")) {
        debugger;
        // For "buy": update category inside right stock
        let stockId = edited.stock_id;
        const allStocksLatest = await axios.get(
          `${process.env.REACT_APP_API_URL}/auth/addstocks`
        );
        let stocksArr = Array.isArray(allStocksLatest.data) ? allStocksLatest.data : [];
        stocksArr = stocksArr.map((stock: any) => ({
          ...stock,
          categories: (() => {
            try {
              if (Array.isArray(stock.categories)) return stock.categories;
              if (typeof stock.categories === "string" && stock.categories.length > 0)
                return JSON.parse(stock.categories);
              return [];
            } catch {
              return [];
            }
          })(),
        }));

        // Find the updated stock (always work with fresh data)
        let stockOriginal = stocksArr.find(s => String(s.id) === String(stockId));

        if (stockOriginal) {
          // Make sure only the date (YYYY-MM-DD) is sent (not full ISO date)
          const formattedDate = edited.date
            ? edited.date.substring(0, 10)
            : "";

          let updatedCategories = (Array.isArray(stockOriginal.categories) ? [...stockOriginal.categories] : []).map(cat => {
            if (
              String(cat.id ?? cat.category_name ?? cat) === String(edited.category_id)
            ) {
              return {
                ...cat,
                qty: edited.qty,
                unit: edited.unit,
                total_amount: edited.amount,
                date: formattedDate // ensure 'date' field here is only the date part
              };
            }
            return cat;
          });

          // Save only if changed; otherwise keep same as before
          updateRes = await axios.put(
            `${process.env.REACT_APP_API_URL}/auth/addstocks`,
            {
              ...stockOriginal,
              categories: updatedCategories,
              // If 'date' field is sent at root, also ensure it's only the date part
              date: formattedDate
            }
          );
        }
      } else if (String(edited.id).startsWith("sell_")) {
        debugger;
        // For "sell": get latest sell record, compare and update only changed fields
        let sellId = String(edited.id).replace(/^sell_/, "");
        const freshSellTxns = await axios.get(
          `https://stockaadvikbackend.aadviktech.com/auth/sellstocks`
        );
        let existing = (Array.isArray(freshSellTxns.data) ? freshSellTxns.data : []).find(
          (s: any) => String(s.id) === String(sellId)
        );
        // Only apply updated values
        // Inside handleSaveEditedTransaction for "sell" type:
updateRes = await axios.put(
  `https://stockaadvikbackend.aadviktech.com/auth/sellstocks/${sellId}`,
  {
    ...existing,
    id: sellId,
    party_id: edited.party_id,
    date: edited.date,
    // Ensure we keep the stock_name as the identifier
    stock_id: edited.stock_id, 
    stock_name: edited.stock_id, 
    category: edited.category_id,
    sell_units: edited.qty,
    sell_amount: edited.amount,
    indirect_charges: edited.indirect_charges,
  }
);
      } else if (String(edited.id).startsWith("withdraw_")) {
        // For "withdraw": get latest withdrawal by id, compare and update only changed fields
        let parts = String(edited.id).split("_");
        let withdrawalId = parts[1];
        const allLatestWithdrawals = await axios.get(`${process.env.REACT_APP_API_URL}/auth/withdrawals`);
        let withdrawalLatest = (Array.isArray(allLatestWithdrawals.data) ? allLatestWithdrawals.data : []).find(
          (w: any) => String(w.id) === String(withdrawalId)
        );
        // Only update changed fields
        updateRes = await axios.put(
          `${process.env.REACT_APP_API_URL}/auth/withdrawals/${withdrawalId}`,
          {
            ...withdrawalLatest,
            id: withdrawalId,
            party_id: edited.party_id,
            date: edited.date,
            amount: edited.amount,
            catrgories: JSON.stringify([edited.category_id]),
            remark: edited.remark ?? "Withdrawal",
          }
        );
      }

      // After update, fetch all fresh data to reload current view
      await fetchAllData();

      setEditTransactionModal(null);
      setFilters((prev) => ({ ...prev })); // force update of memo
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      throw error;
    }
  }

  // ... UI code remains unchanged 

  return (
    <div className="bg-gradient-to-bl from-[#e8f0fe] to-[#eaefec] min-h-screen flex flex-col font-sans relative animate-fade-in">
      {/* ... header etc ... */}
      <header className="w-full flex justify-between items-center px-8 py-5 border-b bg-white shadow-sm sticky top-0 z-40 mr-[350px]">
        <div className="text-2xl tracking-tight font-bold text-[#274472] flex items-center gap-3">
          <AiOutlineFundProjectionScreen className="text-blue-400" /> Asset Investment Tracker
        </div>
        <div className="flex gap-2 md:gap-3">
          <button
            className="flex items-center gap-1 px-3 py-2 bg-[#f3f7fa] rounded hover:bg-blue-50 transition border text-[#333] shadow"
            title="Download as CSV"
            onClick={() => downloadCSV(filteredTransactions, allParties, "csv")}
          >
            <FaDownload className="text-base" />
            Export CSV
          </button>
          <button
            className="flex items-center gap-1 px-3 py-2 bg-[#f3f7fa] rounded hover:bg-blue-50 transition border text-[#333] shadow"
            title="Download for Excel"
            onClick={() => downloadCSV(filteredTransactions, allParties, "excel")}
          >
            <FaDownload className="text-base" />
            Excel
          </button>
          <button
            className="flex items-center gap-1 px-3 py-2 bg-[#f3f7fa] rounded hover:bg-blue-50 transition border text-[#333] shadow"
            title="Print"
            onClick={printPage}
          >
            <MdPrint className="text-lg" /> Print
          </button>
        </div>
      </header>
      <div className="flex relative">
        {/* Side Panel: Transaction List with expand/collapse */}
        <aside
          className={`fixed top-0 right-0 h-full bg-white shadow-lg z-40 transition-all duration-300 flex flex-col
            ${sidePanelCollapsed ? "w-14 mr-0" : "w-[350px] mr-0"}`}
          style={{ minWidth: sidePanelCollapsed ? 56 : 350 }}
        >
          {/* Expand/Collapse Button */}
          <button
            className={`absolute top-4 left-0 -translate-x-full bg-[#e4eaff] hover:bg-[#d2dbfc] 
              text-[#4175EB] border border-[#cfdafe] rounded-r-lg shadow px-2 py-1 transition-all duration-200 z-50`}
            style={{ outline: "none" }}
            onClick={() => setSidePanelCollapsed(v => !v)}
            tabIndex={0}
            aria-label={sidePanelCollapsed ? "Expand transactions panel" : "Collapse transactions panel"}
          >
            {sidePanelCollapsed ? (
              <span className="flex items-center">
                <svg className="h-6 w-6" fill="none" stroke="#4466e7" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7"></path>
                </svg>
              </span>
            ) : (
              <span className="flex items-center">
                <svg className="h-6 w-6" fill="none" stroke="#4466e7" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7"></path>
                </svg>
              </span>
            )}
          </button>
          {!sidePanelCollapsed && (
            <div className="flex-1 overflow-y-auto">
              <PhonePeSidePanel
                transactions={filteredTransactions}
                allStocks={allStocks}
                allParties={allParties}
                categories={categories}
                onSelect={({ transaction, stock, category, party }) => {
                  setSelectedTransaction({ transaction, stock, category, party });
                  setSelectedTxnPanel(transaction.id);
                }}
                selectedTxnId={selectedTxnPanel}
              />
            </div>
          )}
        </aside>
        <main className={`flex-1 w-full px-4 sm:px-10 lg:px-16 py-3 grid gap-7 transition-all duration-300 ${sidePanelCollapsed ? "mr-14" : "mr-[350px]"}`}>
          {/* QUICK FILTERS */}
          <section className="flex flex-wrap items-center gap-2 mb-2">
            <span className="text-[13px] text-[#4897ad] font-semibold mr-2 flex items-center gap-1">
              <TbHistory /> Quick Filters:
            </span>
            {QUICK_FILTERS.map(q => (
              <button
                key={q.key}
                onClick={() => handleQuickFilter(q.key)}
                className={`px-2.5 py-1 rounded-md text-xs font-semibold border transition 
                  ${filters.quick === q.key ? "bg-[#63dbff]/20 border-[#3296bd] text-[#197995]" : 
                  "bg-[#f4fafd] border-[#d3e4f2] text-[#558cb3] hover:bg-[#e6f4ff]"
                  }`}
              >
                {q.label}
              </button>
            ))}
            {filters.quick && (
              <button
                className="ml-2 px-2 py-1 rounded bg-[#fffa] text-[#888] border border-[#ecd5ff] hover:bg-[#fde6ff] text-xs font-semibold"
                onClick={clearQuickFilter}
              >Clear Filter</button>
            )}
          </section>
          {/* ADVANCED FILTERS */}
          <AdvancedFilters
            filters={filters}
            setFilters={setFilters}
            allParties={allParties}
            allStocks={allStocks}
            allCategories={categories}
          />
          {/* STAT CARDS */}
          <section className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-4">
            {statDetails.map((d, idx) => (
              <div
                key={idx}
                className="rounded-xl flex flex-col gap-3 px-6 py-6 shadow relative z-0 border bg-gradient-to-br from-white to-[#f6fafb]"
                style={{
                  borderColor: "#E3E8EE",
                  background: d.color,
                  minHeight: 130,
                  justifyContent: "center",
                }}
              >
                <div className="flex items-center gap-3">
                  <div className="bg-white shadow rounded-full w-11 h-11 flex justify-center items-center border">
                    {d.icon}
                  </div>
                  <div>
                    <div className="font-bold text-lg text-[#18263a]">
                      {typeof d.value === "string"
                        ? d.value
                        : formatMoney(d.value, { symbol: true })}
                    </div>
                    <div className="text-xs font-semibold text-[#758ba7]">
                      {d.label}
                    </div>
                    <div className="text-[10px] text-[#b4bfce]">{d.sub}</div>
                  </div>
                </div>
              </div>
            ))}
          </section>
          {/* Holdings Table */}
          <section className="bg-white rounded-xl shadow p-6 mb-10">
            <div className="font-bold text-[#3F4765] text-xl mb-6 flex items-center gap-2">
              <GiWallet className="text-[#4883e9]/60" /> Current Asset Holdings
              <span className="text-xs ml-2 text-[#7a9ac3]">
                ({assetHoldingsMap.size} items)
              </span>
            </div>
            <div className="overflow-x-auto max-w-full">
              <table className="min-w-[900px] w-full text-xs border-separate border-spacing-y-1">
                <thead>
                  <tr className="bg-[#F7F8FA] border-b">
                    <th className="p-2 text-[#3b4d6f] text-xs font-bold">Stock</th>
                    <th className="p-2 text-[#3b4d6f] text-xs font-bold">Category</th>
                    <th className="p-2 text-[#3b4d6f] text-xs font-bold">Investment Type</th>
                    <th className="p-2 font-bold">Total Bought</th>
                    {/* <th className="p-2 font-bold">Withdrawn</th> */}
                    <th className="p-2 font-bold">Sold</th>
                    <th className="p-2 font-bold">Remaining</th>
                    <th className="p-2 font-bold">Current Price</th>
                    <th className="p-2 font-bold">Avg Buy Price</th>
                    <th className="p-2 font-bold">Holding Value</th>
                    <th className="p-2 font-bold">Trend Graph</th>
                  </tr>
                </thead>
                <tbody>
                  {Array.from(processedHoldings.values()).map((a, idx) => {
                    const key = `${a.stock_id || "-"}_${a.category_id || "-"}`;
                    const isNew = recentTxnKeys.has(key);
                    return (
                      <tr
                        key={key}
                        // <-- Add yellow highlight for new holdings
                        className={
                          (isNew ? "bg-yellow-50 ring-1 ring-yellow-300 " : "") +
                          (idx % 2 === 0 ? "bg-[#f6fafb]" : "bg-white")
                        }
                      >
                        <td className="p-2">
                          {a.stock_id ? (
                            <Link
                              className="underline text-[#226C92] font-semibold"
                              to="/BalanceSheet"
                            >
                                {
                                  typeof a.stock?.id === "object"
                                    ? "-"
                                    : Array.isArray(a.category)
                                      ? (
                                          a.category[0]?.stock_name ||
                                          a.category[0]?.stockId ||
                                          "-"
                                        )
                                      : typeof a.category === "object" && a.category !== null
                                        ? (
                                            a.category.stockName ||
                                            a.category.stock_name ||
                                            a.category.stockId ||
                                            a.stock?.id ||
                                            "-"
                                          )
                                        : a.category?.stock_name ||
                                          a.stock?.id ||
                                          "-"
                                }
                            </Link>
                          ) : (
                            "-"
                          )}
                          {/* Show "NEW" pill if new */}
                          {isNew &&
                            <span className="ml-2 px-2 py-0.5 rounded-full text-xs font-bold bg-yellow-300 text-yellow-900 animate-pulse">NEW</span>
                          }
                        </td>
                        <td className="p-2">
                          {typeof a.category?.category_name === "object"
                            ? "-"
                            : a.category?.category_name ||
                              (typeof a.category?.category === "object"
                                ? "-"
                                : a.category?.category) ||
                              (typeof a.category?.stock_name === "object"
                                ? "-"
                                : a.category?.stock_name) ||
                              (typeof a.category_id === "object"
                                ? "-"
                                : a.category_id) ||
                              "-"}
                        </td>
                         <td className="p-2">
                          <span className={clsx(
                            "px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider",
                            a.investment_type === 'direct' ? "bg-blue-100 text-blue-700" : 
                            a.investment_type === 'structure' ? "bg-purple-100 text-purple-700" : 
                            "bg-gray-100 text-gray-600"
                          )}>
                            {a.investment_type || "-"}
                          </span>
                        </td>
                        <td className="p-2 font-bold text-[#16a34a]">
                          {/* {a.total_bought}{" "}
                          {typeof a.unit === "object" ? "-" : a.unit} */}
                          {a.total_bought}
                        </td>
                        {/* <td className="p-2 font-bold text-[#fbbf24]">
                          {a.total_withdraw}{" "}
                          {typeof a.unit === "object" ? "-" : a.unit}
                        </td> */}
                        <td className="p-2 font-bold text-[#ef4444]">
                          {/* {a.total_sold} {typeof a.unit === "object" ? "-" : a.unit} */}
                          {a.total_sold}
                        </td>
                        <td className="p-2 font-semibold text-[#11977D]">
                          {/* {a.remaining_qty}{" "}{typeof a.unit === "object" ? "-" : a.unit} */}
                          {a.remaining_qty}
                        </td>
                        <td className="p-2 font-normal">
                          {formatMoney(a.current_price, { symbol: true })}
                        </td>
                        <td className="p-2 font-normal text-[#5751d6]">
                          {formatMoney(a.avg_buy_price ?? 0, { symbol: true })}
                        </td>
                        <td className="p-2 font-bold text-[#1457f1]">
                          {formatMoney(a.holding_value, { symbol: true })}
                        </td>
                        <td>
                          <div style={{ width: 68, height: 24 }}>
                            <Line
                              data={{
                                labels: a.history.map((tr, i) => i + 1),
                                datasets: [
                                  {
                                    label: "Qty",
                                    data: a.history.map((tr) => tr.qty),
                                    backgroundColor: "#93DEFF99",
                                    borderColor: "#19a0fa",
                                    pointRadius: 0,
                                    tension: 0.5,
                                  },
                                ],
                              }}
                              options={{
                                responsive: false,
                                plugins: { legend: { display: false } },
                                scales: {
                                  x: { display: false },
                                  y: { display: false },
                                },
                                elements: {
                                  line: { borderWidth: 2 },
                                  point: { radius: 1 },
                                },
                              }}
                              height={24}
                              width={68}
                            />
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                  {assetHoldingsMap.size === 0 && (
                    <tr>
                      <td
                        colSpan={10}
                        className="p-4 text-center text-[#9CA3AF]"
                      >
                        No asset holdings found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>
          {/* Ledger / Transaction Table */}



          <section className="bg-white rounded-xl shadow p-6 mb-6">
            <div className="flex items-center justify-between mb-3">
              <span className="font-bold text-[#3F4765] text-xl flex items-center gap-2">
                <FaExchangeAlt className="text-[#357fb2]" /> All Transactions Ledger
                <span className="text-xs text-[#7a9ac3]">
                  ({filteredTransactions.length} entries)
                </span>
              </span>
              <div className="flex gap-3 flex-wrap">
                <label className="flex items-center gap-1 text-xs font-semibold text-[#3F4765]">
                  Columns:
                  {Object.keys(visibleCols).map((col) => (
                    <span key={col} className="flex items-center gap-1 ml-2">
                      <input
                        type="checkbox"
                        checked={visibleCols[col as keyof typeof visibleCols]}
                        onChange={() =>
                          setVisibleCols((prev) => ({
                            ...prev,
                            [col]: !prev[col as keyof typeof visibleCols],
                          }))
                        }
                      />{" "}
                      {col
                        .replace(/_/g, " ")
                        .replace(/\b\w/g, ch => ch.toUpperCase())}
                    </span>
                  ))}
                </label>
              </div>
            </div>
            <div className="rounded overflow-x-auto max-w-full">
              <table className="min-w-[950px] w-full text-xs border-separate border-spacing-y-1">
                <thead>
                  <tr className="bg-[#f3f7fa] border-b text-[#42547b]">
                    {visibleCols.date && <th className="p-2">Date</th>}
                    {visibleCols.stock && <th className="p-2">Stock</th>}
                    {visibleCols.stock && <th className="p-2">Investment Type</th>}
                    {visibleCols.category && <th className="p-2">Category</th>}
                    {visibleCols.party && <th className="p-2">Party</th>}
                    {visibleCols.type && <th className="p-2">Type</th>}
                    {visibleCols.qty && <th className="p-2">Qty</th>}
                    {visibleCols.unit && <th className="p-2">Unit</th>}
                    {visibleCols.amount && (
                      <th className="p-2">Amount (₹)</th>
                    )}
                    {visibleCols.avg_buy_price && (
                      <th className="p-2">Avg Buy Price</th>
                    )}
                    {visibleCols.remark && <th className="p-2">Remark</th>}
                    <th className="p-2">Detail</th>
                    <th className="p-2">Edit</th>
                    <th className="p-2">Delete</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTransactions.length ? (
                    filteredTransactions.map((t, idx) => {
                      const { stock, category } = findStockAndCategory(
                        t.stock_id,
                        t.category_id
                      );
                      const party = allParties.find(
                        (p) => String(p.id) === String(t.party_id)
                      );
                      let avgBuyPrice = undefined;
                      if (visibleCols.avg_buy_price) {
                        const ah = assetHoldingsMap.get(`${t.stock_id || "-"}_${t.category_id || "-"}`);
                        avgBuyPrice = ah?.avg_buy_price ?? undefined;
                      }
                      // highlight recent txns in table
                      const isNewTxn = isRecent(t.date);
                      return (
                        <tr
                          key={t.id}
                          className={
                            (isNewTxn ? "bg-yellow-50 ring-1 ring-yellow-300 " : "") +
                            (idx % 2 === 0 ? "bg-[#ecf7fb]" : "bg-[#fbfbfd]")
                          }
                        >
                          {visibleCols.date && (
                            <td className="p-2 whitespace-nowrap">
                              {t.date}
                              {isNewTxn &&
                                <span className="ml-2 px-1.5 py-0.5 rounded-full bg-yellow-300 text-yellow-900 text-[10px] font-extrabold animate-pulse inline-block align-middle">NEW</span>
                              }
                            </td>
                          )}
                          {visibleCols.stock && (
                            <td className="p-2">
                              {   (typeof category?.stock_name === "object"
                                    ? "-"
                                    : category?.stock_name)}
                            </td>
                          )}
                          {visibleCols.investment_type && (
                            <td className="p-2">
                              <span className={clsx(
                                "px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider",
                                t.investment_type === 'direct' ? "bg-blue-100 text-blue-700" : 
                                t.investment_type === 'structure' ? "bg-purple-100 text-purple-700" : 
                                "bg-gray-100 text-gray-600"
                              )}>
                                {t.investment_type || "-"}
                              </span>
                            </td>
                          )}
                          {visibleCols.category && (
                            <td className="p-2">
                              {typeof category?.category_name === "object"
                                ? "-"
                                : category?.category_name ||
                                  (typeof category?.category === "object"
                                    ? "-"
                                    : category?.category) ||
                                  (typeof category?.stock_name === "object"
                                    ? "-"
                                    : category?.stock_name) ||
                                  (typeof t.category_id === "object"
                                    ? "-"
                                    : t.category_id) ||
                                  "-"}
                            </td>
                          )}
                          {visibleCols.party && (
                            <td className="p-2">
                              {typeof party?.name === "object"
                                ? "-"
                                : party?.name ??
                                  (typeof t.party_id === "object"
                                    ? "-"
                                    : t.party_id) ??
                                  "-"}
                            </td>
                          )}
                          {visibleCols.type && (
                            <td className="p-2">
                              {t.type === "buy" && (
                                <span className="inline-flex items-center gap-1 text-[#36a859] font-bold">
                                  <BsBoxArrowInDown /> Buy
                                </span>
                              )}
                              {t.type === "sell" && (
                                <span className="inline-flex items-center gap-1 text-[#F04959] font-bold">
                                  <BsBoxArrowUpRight /> Sell
                                </span>
                              )}
                              {t.type === "withdraw" && (
                                <span className="inline-flex items-center gap-1 text-[#c87b12] font-bold">
                                  <TbZoomMoney /> Withdraw
                                </span>
                              )}
                            </td>
                          )}
                          {visibleCols.qty && (
                            <td className="p-2">{t.qty}</td>
                          )}
                          {visibleCols.unit && (
                            <td className="p-2">
                              {typeof t.unit === "object"
                                ? "-"
                                : t.unit ?? "-"}
                            </td>
                          )}
                          {visibleCols.amount && (
                            <td className="p-2 font-semibold text-[#336995]">
                              {typeof t.amount === "object"
                                ? "0.00"
                                : t.amount
                                ? Number(t.amount).toLocaleString("en-IN", {
                                    maximumFractionDigits: 2,
                                  })
                                : "0.00"}
                            </td>
                          )}
                          {visibleCols.avg_buy_price && (
                            <td className="p-2 font-semibold text-[#856cdb]">{avgBuyPrice ? formatMoney(avgBuyPrice, { symbol: true }) : "-"}</td>
                          )}
                          {visibleCols.remark && (
                            <td className="p-2 text-[#717E98]">
                              {typeof t.remark === "object"
                                ? "-"
                                : t.remark ?? "-"}
                            </td>
                          )}
                          <td className="p-2 text-center">
                            <button
                              className="text-blue-500 hover:underline text-xs font-semibold"
                              title="View Details"
                              onClick={() => {
                                setSelectedTransaction({
                                  transaction: t,
                                  stock,
                                  category,
                                  party,
                                });
                                setSelectedTxnPanel(t.id);
                              }}
                            >
                              Detail
                            </button>
                          </td>
                          <td className="p-2 text-center">
                            <button
                              className="text-yellow-600 hover:underline hover:text-yellow-700 text-xs font-semibold flex items-center gap-1"
                              title="Edit Transaction"
                              onClick={() => {
                                let editPage = "";
                                if (t.type === "buy") {
                                  editPage = "edit-stock";
                                } else if (t.type === "sell") {
                                  editPage = "edit-sell";
                                } else if (t.type === "withdraw" || t.type === "withdrawl") {
                                  editPage = "edit-widthsrawl";
                                }
                                if (editPage) {
                                  window.location.href = `/${editPage}/?party_id=${t.party_id}&category=${t.category_id}&stock_id=${t.stock_id}`;
                                }
                              }}
                            >
                              <FaRegEdit className="inline" /> Edit
                            </button>
                          </td>
                          <td className="p-2 text-center">
                            <button
                              className="text-red-600 hover:bg-red-50 hover:text-red-800 text-xs font-semibold flex items-center justify-center gap-1 px-3 py-1 rounded transition-all duration-150 border border-transparent hover:border-red-200 shadow-sm"
                              title="Delete Transaction"
                              onClick={() => handleDeleteTransaction(t.id)}
                            >
                              <svg 
                                width="16"
                                height="16"
                                viewBox="0 0 16 16"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                                className="inline mr-1"
                              >
                                <rect x="3.2" y="7.1" width="1.4" height="5" rx=".7" fill="#e3342f"/>
                                <rect x="6.3" y="7.1" width="1.4" height="5" rx=".7" fill="#e3342f"/>
                                <rect x="9.4" y="7.1" width="1.4" height="5" rx=".7" fill="#e3342f"/>
                                <rect x="2" y="4.7" width="12" height="1.4" rx=".7" fill="#e3342f"/>
                                <rect x="5.2" y="2" width="5.6" height="2" rx="1" fill="#e3342f"/>
                                <rect x="3.2" y="4" width="9.6" height="9.6" rx="2" stroke="#e3342f" strokeWidth="1" fill="none"/>
                              </svg>
                              <span>Delete</span>
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td
                        colSpan={
                          Object.values(visibleCols).filter((x) => x).length + 2
                        }
                        className="p-6 text-center text-[#B1B5C4] text-sm"
                      >
                        No transaction found
                      </td>
                    </tr>
                  )}
                </tbody>
                {filteredTransactions.length > 0 && (
                  <tfoot>
                    <tr className="bg-[#ecf7fb] font-bold text-[#436095] border-t">
                      {visibleCols.date && <td className="p-2 text-right" colSpan={1}>Total</td>}
                      {visibleCols.stock && <td></td>}
                      {visibleCols.category && <td></td>}
                      {visibleCols.party && <td></td>}
                      {visibleCols.type && <td></td>}
                      {visibleCols.qty && (
                        <td className="p-2">{tableSum.qty}</td>
                      )}
                      {visibleCols.unit && <td></td>}
                      {visibleCols.amount && (
                        <td className="p-2 text-[#218758]">{formatMoney(tableSum.amount, { symbol: true })}</td>
                      )}
                      {visibleCols.avg_buy_price && <td></td>}
                      {visibleCols.remark && <td></td>}
                      <td></td>
                      <td></td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </section>



          {/* VISUALS */}
          <section className="grid grid-cols-1 lg:grid-cols-5 gap-10 mt-8 mb-4 animate-fade-in">
            {/* Pie: Category */}
            <div className="relative bg-gradient-to-tl from-[#fff3ec] via-[#e4efff] to-[#f1f8ff] shadow-xl rounded-3xl p-8 flex flex-col items-center col-span-2 border-2 border-[#e9e9fc] hover:scale-[1.03] transition-all duration-200 group overflow-hidden min-h-[350px]">
              <span className="absolute -top-10 -left-12 w-[170px] h-[170px] bg-gradient-to-tr from-[#b7acf1] via-[#74daf8] to-[#fff3ec] opacity-10 rounded-full pointer-events-none"></span>
              <div className="mb-6 relative z-10 flex items-center gap-2">
                <FaChartPie className="w-7 h-7 text-[#8357ff] drop-shadow-md" />
                <span className="font-extrabold text-transparent text-lg bg-clip-text bg-gradient-to-r from-[#633fef] to-[#7ae883] tracking-wide drop-shadow-lg">Holdings by Category</span>
              </div>
              <div className="w-[220px] h-[220px] flex items-center justify-center relative z-10">
                <Doughnut
                  data={{
                    labels: Array.from(processedHoldings.values()).map((a) =>
                      typeof a.category?.category_name === "object"
                        ? "-"
                        : a.category?.category_name ||
                          (typeof a.category?.category === "object"
                            ? "-"
                            : a.category?.category) ||
                          (typeof a.category?.stock_name === "object"
                            ? "-"
                            : a.category?.stock_name) ||
                          (typeof a.category_id === "object"
                            ? "-"
                            : a.category_id) ||
                          "-"
                    ),
                    datasets: [
                      {
                        data: Array.from(processedHoldings.values()).map((a) =>
                          typeof a.holding_value === "object" ? 0 : a.holding_value
                        ),
                        backgroundColor: [
                          "rgba(134,85,255,0.98)",
                          "rgba(24,189,202,0.9)",
                          "rgba(255,174,63,0.95)",
                          "rgba(80,216,255,0.88)",
                          "rgba(253,238,117,0.85)",
                          "rgba(255,124,163,0.92)",
                          "rgba(70,201,156,0.91)",
                          "rgba(249,100,67,0.93)",
                        ],
                        borderWidth: 4,
                        borderColor: "#fff",
                        hoverOffset: 10,
                      },
                    ],
                  }}
                  options={{
                    plugins: {
                      legend: {
                        position: "bottom",
                        labels: {
                          color: "#5c6698",
                          font: { size: 13, weight: "bold" },
                          boxWidth: 20,
                          padding: 18,
                          usePointStyle: true,
                        },
                      },
                      tooltip: {
                        backgroundColor: "#fff",
                        titleColor: "#8357ff",
                        bodyColor: "#5c8e74",
                        borderColor: "#c2aaff",
                        borderWidth: 2,
                        bodyFont: { weight: "bold", size: 13 }
                      }
                    },
                    cutout: 82,
                  }}
                />
                <div className="absolute left-0 top-0 w-full h-full flex flex-col items-center justify-center pointer-events-none">
                  <span className="text-[2.2rem] text-[#8471ff]/10 font-black tracking-widest select-none">
                    <GiWallet />
                  </span>
                </div>
              </div>
            </div>
            {/* Timeline investment visual */}
            <div className="relative bg-gradient-to-b from-[#e1f2ff] via-[#fffdf9] to-[#d3f6ed] shadow-2xl rounded-3xl p-8 flex flex-col items-center col-span-3 border-2 border-[#e4eaff] overflow-visible min-h-[350px]">
              <span className="absolute -top-12 right-0 w-40 h-40 bg-gradient-to-tr from-[#ffbca5] via-[#f9f3fc] to-[#73d6ca] opacity-30 rounded-full"></span>
              <div className="mb-6 flex items-center gap-2 z-10">
                <AiOutlineFundProjectionScreen className="text-[#41cee8] text-2xl drop-shadow-lg" />
                <span className="font-extrabold text-transparent text-lg bg-clip-text bg-gradient-to-l from-[#299da8] to-[#4175EB]">Investment Timeline</span>
              </div>
              <div className="w-full overflow-x-auto relative z-10" style={{ minHeight: "220px" }}>
                <Line
                  data={{
                    labels: filteredTransactions.map((t) =>
                      typeof t.date === "object" ? "-" : t.date
                    ),
                    datasets: [
                      {
                        label: "Invested",
                        data: filteredTransactions.map((t) =>
                          t.type === "buy"
                            ? typeof t.amount === "object"
                              ? 0
                              : t.amount
                            : 0
                        ),
                        backgroundColor: "rgba(63,163,255,0.05)",
                        borderColor: "#29B8C6",
                        fill: true,
                        pointRadius: 3.5,
                        pointStyle: 'rectRot',
                        pointBackgroundColor: "#2edbeb",
                        pointBorderWidth: 2,
                        tension: 0.36,
                      },
                      {
                        label: "Withdrawn",
                        data: filteredTransactions.map((t) =>
                          t.type === "withdraw"
                            ? typeof t.amount === "object"
                              ? 0
                              : t.amount
                            : 0
                        ),
                        backgroundColor: "rgba(232,247,225,0.18)",
                        borderColor: "#7bedaf",
                        fill: true,
                        pointRadius: 3.5,
                        pointStyle: 'star',
                        pointBackgroundColor: "#7bedaf",
                        pointBorderWidth: 2,
                        tension: 0.33,
                      },
                      {
                        label: "Sold",
                        data: filteredTransactions.map((t) =>
                          t.type === "sell"
                            ? typeof t.amount === "object"
                              ? 0
                              : t.amount
                            : 0
                        ),
                        backgroundColor: "rgba(255,182,203,0.18)",
                        borderColor: "#ff417a",
                        fill: true,
                        pointRadius: 3,
                        pointStyle: 'cross',
                        pointBackgroundColor: "#ff417a",
                        pointBorderWidth: 2,
                        tension: 0.28,
                      },
                    ],
                  }}
                  options={{
                    responsive: true,
                    plugins: {
                      legend: {
                        position: "top",
                        labels: {
                          color: "#668ec3", font: { size: 14, weight: "bold" },
                          boxWidth: 22, usePointStyle: true, padding: 16
                        }
                      },
                      tooltip: {
                        backgroundColor: "#fff",
                        titleColor: "#5646c5",
                        bodyColor: "#34aea3",
                        borderColor: "#b4d8fe",
                        borderWidth: 2,
                        bodyFont: { weight: "bold", size: 14 }
                      }
                    },
                    scales: {
                      y: {
                        beginAtZero: true,
                        ticks: { color: "#3996b3", font: { size: 12, weight: 600 } },
                        grid: { color: "#f0fafb" }
                      },
                      x: {
                        ticks: { color: "#6b6f80", font: { size: 12, weight: 700 } },
                        grid: { color: "#f4f8fa" },
                      },
                    },
                    maintainAspectRatio: false,
                  }}
                  height={220}
                />
              </div>
            </div>
            {/* Bar: Holdings by Stock */}
            {barData.labels.length > 1 && (
              <div className="relative min-h-[360px] mt-10 bg-gradient-to-bl from-[#eaf6ff] via-[#fff0fc] to-[#daf1e9] shadow-[0_6px_32px_0_rgba(34,178,190,0.1)] rounded-[2.5rem] px-10 py-10 flex flex-col items-center lg:col-span-5 overflow-hidden border-2 border-[#cddbee] isolate">
                <span className="absolute left-10 -top-16 w-64 h-64 bg-gradient-to-tl from-[#38eeff] via-[#aadcfc] to-[#ffade9] opacity-20 rounded-full blur-md pointer-events-none z-0"></span>
                <span className="absolute -right-12 bottom-2 w-44 h-44 bg-gradient-to-br from-[#ddffe4] via-[#e9dfff] to-[#fed894] opacity-20 rounded-full blur-md pointer-events-none z-0"></span>
                <div className="absolute right-12 top-6 text-[#a1c6fa] pointer-events-none select-none z-0 scale-125">
                  <AiOutlineFundProjectionScreen size={58} opacity={0.09} />
                </div>
                <div className="mb-7 flex flex-col items-center z-10">
                  <span className="block text-[17px] font-extrabold tracking-widest uppercase bg-clip-text text-transparent bg-gradient-to-r from-[#32C6DC] via-[#488cfb] to-[#845ec2] mb-2 flex items-center gap-2 drop-shadow">
                    <GiWallet className="text-[#57cab2]/80" size={26}/> Holdings by Stock
                  </span>
                  <div className="w-24 h-2 rounded-full bg-gradient-to-r from-[#32C6DC] via-[#488cfb] to-[#845ec2] mt-2 mb-2 opacity-80 shadow-lg" />
                </div>
                <div className="w-full relative z-10 flex flex-col items-center" style={{ minHeight: "220px" }}>
                  <div className="absolute top-3 left-4">
                    <span className="inline-flex items-center gap-1 text-xs bg-[#f4f7ec] text-[#269972] px-4 py-1 rounded-lg shadow-sm border border-[#cbede7] font-bold uppercase tracking-widest animate-bounce-slow">
                      <FaRegMoneyBillAlt className="mr-1" />Value
                    </span>
                  </div>
                  <Bar
                    data={barData}
                    options={{
                      responsive: true,
                      plugins: {
                        legend: { display: false },
                        tooltip: {
                          callbacks: {
                            label: function(context: any) {
                              return `₹${Number(context.raw).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
                            }
                          },
                          backgroundColor: "#fff",
                          titleColor: "#0a71ed",
                          bodyColor: "#53b181",
                          borderColor: "#91f3d3",
                          borderWidth: 2,
                          cornerRadius: 8,
                          padding: 13
                        },
                        title: { display: false },
                      },
                      scales: {
                        x: {
                          grid: { color: "#ecf6fc" },
                          ticks: { color: "#4678a7", font: { weight: 700, size: 14 } },
                          border: { color: "#e8eef7" },
                        },
                        y: {
                          grid: { color: "#f3fcf8" },
                          ticks: {
                            color: "#269972",
                            font: { weight: 700, size: 12 },
                            callback: (val: number | string) => Number(val).toLocaleString("en-IN")
                          },
                          beginAtZero: true,
                          border: { color: "#e8eef7" }
                        },
                      },
                      maintainAspectRatio: false,
                      animation: {
                        duration: 1450,
                        easing: "easeOutElastic",
                      },
                    }}
                    height={230}
                  />
                </div>
              </div>
            )}
            {/* ADVANCED: Yearly overview - mini table */}
            <div className="col-span-full mt-8">
              <div className="bg-white rounded-lg shadow flex flex-col md:flex-row items-center md:justify-between p-4 gap-5 border border-[#e4e9f7]">
                <span className="font-bold text-[#467b99] flex items-center gap-2">
                  <TbHistory className="inline text-[#b48aea]"/> Yearly Investment Summary
                </span>
                <div className="overflow-x-auto">
                  <table className="text-xs min-w-[350px] text-[#657697] mx-auto">
                    <thead>
                      <tr>
                        {Object.keys(getYearlySummary()).map(yr => (
                          <th key={yr} className="px-3 py-2">{yr}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        {Object.values(getYearlySummary()).map((amt, i) => (
                          <td key={i} className="px-3 py-1 font-bold">{formatMoney(amt, { compact: true, symbol: true })}</td>
                        ))}
                      </tr>
                    </tbody>
                  </table>
                </div>
                <span className="hidden md:inline text-xs text-[#8ca8e2]">Scroll above for monthly & other visualizations.</span>
              </div>
            </div>
          </section>
        </main>
      </div>
      {/* Detail Modal */}
      {selectedTransaction && (
        <TransactionDetailModal
          transaction={selectedTransaction.transaction}
          onClose={() => setSelectedTransaction(null)}
          stock={selectedTransaction.stock}
          category={selectedTransaction.category}
          party={selectedTransaction.party}
        />
      )}
      {/* EDIT POPUP MODAL */}
      {editTransactionModal && (
        <TransactionEditModal
          transaction={editTransactionModal.transaction}
          onClose={() => setEditTransactionModal(null)}
          onSave={handleSaveEditedTransaction}
        />
      )}
      {/* Sticky footer with legend and info */}
      <footer className="sticky left-0 bottom-0 w-full z-50 shadow-[0_-3px_16px_-4px_#adc7f744] bg-[#ecf5fd] px-8 py-2 flex flex-wrap items-center text-[13px] gap-7 font-semibold text-[#436095] mr-[350px]">
        <div className="flex gap-3 items-center">
          <FaRegArrowAltCircleDown className="text-[#29b87f]" /> Withdraw
          <span className="mx-2">|</span>
          <FaRegArrowAltCircleUp className="text-[#ed0e25]" /> Sell
          <span className="mx-2">|</span>
          <IoWalletSharp className="text-[#F49A40]" /> Invest
        </div>
        <span className="flex-1 text-[#7da6c1] ml-3 min-w-[160px]">
          Tip: Use advanced filters, quick presets & dynamic charts to analyze your investments deeper!
        </span>
      </footer>
      {isLoading && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-white/80">
          <div className="bg-gradient-to-tr from-[#ecfcff] via-[#e3eafe] to-[#f9fbfe] p-10 rounded-2xl shadow-lg flex flex-col items-center">
            <span className="text-2xl mb-1 text-[#2296ec] animate-bounce">
              <TbHistory />
            </span>
            <span className="font-bold text-[#357fb2] text-lg tracking-wider mb-2">Loading data...</span>
            <span className="text-[#8dbad6] text-xs">Please wait</span>
          </div>
        </div>
      )}
    </div>
  );
}

export default UserHistory;
